/**
 * ChannelApp — the app root. Clerk auth (chrome) → the persistent Chrome frame
 * (header always present) wrapping the routed body (channel Shell / profile).
 * The client is a channel: no turns shell, no iframe runner.
 *
 * Command-level loading/error use the ONE canonical StatusModal (components/) —
 * never a per-page spinner.
 */
import { Routes, Route, Navigate } from "react-router-dom";

import { useAuth } from "./hooks/useAuth";
import { Chrome } from "./channel/Chrome";
import { Shell } from "./channel/Shell";
import { StatusModal } from "./components/StatusModal";
import { OrganizationAccessScreen } from "./components/OrganizationAccessScreen";

export default function ChannelApp() {
  const {
    isInitializing,
    statusText,
    user,
    activeUser,
    needsOrganization,
    isCheckingOrganization,
    organizationSelection,
    organizationSelectionError,
    selectOrganization,
    retryOrganizationAccess,
    signIn,
    signOut,
    activeOrg,
    tenantAttachmentError,
    clearTenantAttachmentError,
  } = useAuth();

  // Page-level loading → the canonical "waiting" status (pulsing rings). The text
  // is the REAL current phase (statusText from useAuth) — no fabricated default;
  // StatusModal throws if it ever gets none.
  if (isInitializing) {
    return <StatusModal isOpen type="waiting" title={statusText} onClose={() => {}} />;
  }

  if (needsOrganization) {
    return (
      <OrganizationAccessScreen
        selection={organizationSelection}
        error={organizationSelectionError}
        isChecking={isCheckingOrganization}
        onSelect={(orgId) => void selectOrganization(orgId)}
        onRetry={() => void retryOrganizationAccess()}
        onSignOut={() => void signOut()}
      />
    );
  }

  if (!user) {
    return (
      <div style={{ display: "grid", placeItems: "center", height: "100vh" }}>
        <button
          onClick={() => signIn()}
          style={{ padding: "10px 22px", borderRadius: 10, border: "none", background: "var(--accent-primary)", color: "#fff", cursor: "pointer", fontSize: 14, fontWeight: 600 }}
        >
          Sign in
        </button>
      </div>
    );
  }

  // The Chrome layout renders the persistent header on EVERY route; the routed
  // body (Shell / UserProfile) renders below it via <Outlet>.
  if (!activeUser || !activeOrg) {
    return <StatusModal isOpen type="waiting" title={statusText} onClose={() => {}} />;
  }
  return (
    <>
      <Routes>
        <Route element={
          <Chrome
            user={user}
            activeUser={activeUser}
            activeOrg={activeOrg}
          />
        }>
          <Route index element={<Shell />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
      <StatusModal
        isOpen={!!tenantAttachmentError}
        type="error"
        title="Tenant attachment failed"
        message={tenantAttachmentError || undefined}
        onClose={clearTenantAttachmentError}
      />
    </>
  );
}
